import React, { useState, useEffect } from 'react';
import { ApiDocEndpoint, ApiHistoryEntry } from '@/types/ApiDocs.ts';
import { cn } from '@/lib/utils.ts';
import { Button } from '@/components/ui/button.tsx';
import { Copy, Play, RotateCcw, Globe, Terminal, Code2 } from 'lucide-react';
import { CopyButton, CodePanel, ParamField } from './shared.tsx';
import { buildCodeSnippets, METHOD_COLORS } from '../utils.ts';

interface EndpointPanelProps {
    endpoint: ApiDocEndpoint;
    token: string;
    onHistoryAdd: (entry: ApiHistoryEntry) => void;
}

export function EndpointPanel({ endpoint, token, onHistoryAdd }: EndpointPanelProps) {
    const [paramVals, setParamVals] = useState<Record<string, string>>({});
    const [activeTab, setActiveTab] = useState<'curl' | 'python'>('curl');
    const [response, setResponse] = useState<{ status: number; data: unknown } | null>(null);
    const [loading, setLoading] = useState(false);
    const [showResponse, setShowResponse] = useState(false);

    // אתחל ערכי ברירת מחדל
    useEffect(() => {
        const defaults: Record<string, string> = {};
        endpoint.params_schema.forEach((p) => {
            if (p.default) defaults[p.name] = p.default;
        });
        setParamVals(defaults);
        setResponse(null);
        setShowResponse(false);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [endpoint.id]);

    const snippets = buildCodeSnippets(endpoint, token, paramVals);

    const handleRun = async () => {
        setLoading(true);
        setShowResponse(true);
        try {
            let resolvedUrl = endpoint.url_pattern;
            endpoint.params_schema
                .filter((p) => p.in === 'path')
                .forEach((p) => {
                    resolvedUrl = resolvedUrl.replace(
                        `{${p.name}}`,
                        paramVals[p.name] || `:${p.name}`,
                    );
                });

            const qp = new URLSearchParams();
            if (endpoint.method === 'GET') {
                endpoint.params_schema
                    .filter((p) => p.in !== 'path')
                    .forEach((p) => {
                        const v = paramVals[p.name] || p.default || '';
                        if (v) qp.set(p.name, v);
                    });
            }

            const bodyObj: Record<string, string> = {};
            if (endpoint.method !== 'GET') {
                endpoint.params_schema
                    .filter((p) => p.in !== 'path')
                    .forEach((p) => {
                        const v = paramVals[p.name] || p.default || '';
                        if (v) bodyObj[p.name] = v;
                    });
            }

            const qs = qp.toString() ? `?${qp.toString()}` : '';
            const headers: Record<string, string> = { Accept: 'application/json' };
            if (token) headers['Authorization'] = `Token ${token}`;
            if (endpoint.method !== 'GET') headers['Content-Type'] = 'application/json';

            const res = await fetch(`${resolvedUrl}${qs}`, {
                method: endpoint.method,
                headers,
                credentials: 'omit',
                body: endpoint.method !== 'GET' ? JSON.stringify(bodyObj) : undefined,
            });

            let data: unknown;
            const contentType = res.headers.get('content-type') || '';
            if (contentType.includes('application/json')) {
                data = await res.json();
            } else {
                data = await res.text();
            }

            setResponse({ status: res.status, data });
            const entry: ApiHistoryEntry = {
                id: `${Date.now()}-${Math.random()}`,
                timestamp: Date.now(),
                endpointId: endpoint.id,
                endpointName: endpoint.name,
                method: endpoint.method,
                url: `${resolvedUrl}${qs}`,
                status: res.status,
                params: { ...paramVals },
                response: data,
            };
            onHistoryAdd(entry);
        } catch (e) {
            setResponse({ status: 0, data: { error: String(e) } });
        } finally {
            setLoading(false);
        }
    };

    const TABS = [
        { key: 'curl' as const, label: 'cURL', icon: Terminal },
        { key: 'python' as const, label: 'Python', icon: Code2 },
    ];

    return (
        <div className="flex flex-col gap-6">
            {/* כותרת */}
            <div className="flex flex-col gap-2">
                <div className="flex items-center gap-3">
                    <span
                        className={cn(
                            'px-2 py-1 rounded text-xs font-bold border shrink-0',
                            METHOD_COLORS[endpoint.method],
                        )}
                    >
                        {endpoint.method}
                    </span>
                    <h2 className="text-xl font-bold text-gray-800">{endpoint.name}</h2>
                </div>
                <div
                    className="flex items-center gap-0 bg-[#1e1e2e] rounded-lg overflow-hidden self-end max-w-full"
                    dir="ltr"
                >
                    <code className="text-sm text-green-300 font-mono px-4 py-2 select-all">
                        {window.location.origin}{endpoint.url_pattern}
                    </code>
                    <button
                        onClick={() =>
                            navigator.clipboard.writeText(
                                window.location.origin + endpoint.url_pattern,
                            )
                        }
                        className="px-3 py-2 text-gray-400 hover:text-white hover:bg-white/10 transition-colors border-r border-white/10 shrink-0"
                        title="העתק URL"
                    >
                        <Copy className="size-4" />
                    </button>
                </div>
            </div>

            {endpoint.description && (
                <p className="text-gray-600 text-sm leading-relaxed bg-gray-50 rounded-lg px-4 py-3">
                    {endpoint.description}
                </p>
            )}

            <div className="grid grid-cols-1 xl:grid-cols-2 gap-6">
                {/* עמודת פרמטרים */}
                <div className="flex flex-col gap-4">
                    <h3 className="font-semibold text-gray-700 text-sm flex items-center gap-2">
                        <Globe className="size-4" /> פרמטרים
                    </h3>
                    {endpoint.params_schema.length === 0 ? (
                        <p className="text-gray-400 text-sm">אין פרמטרים לאנדפוינט זה</p>
                    ) : (
                        endpoint.params_schema.map((p) => (
                            <ParamField
                                key={p.name}
                                param={p}
                                value={paramVals[p.name] ?? p.default ?? ''}
                                onChange={(v) =>
                                    setParamVals((prev) => ({ ...prev, [p.name]: v }))
                                }
                            />
                        ))
                    )}
                    <Button
                        onClick={handleRun}
                        disabled={loading}
                        className="w-full bg-green-600 hover:bg-green-700 text-white flex items-center gap-2 justify-center"
                    >
                        {loading ? (
                            <RotateCcw className="size-4 animate-spin" />
                        ) : (
                            <Play className="size-4" />
                        )}
                        {loading ? 'שולח...' : 'הרץ'}
                    </Button>
                </div>

                {/* עמודת קוד + תגובה */}
                <div className="flex flex-col gap-4">
                    {/* Code snippets */}
                    <div>
                        <div className="flex gap-1 mb-2">
                            {TABS.map((t) => (
                                <button
                                    key={t.key}
                                    onClick={() => setActiveTab(t.key)}
                                    className={cn(
                                        'flex items-center gap-1.5 px-3 py-1.5 text-xs rounded-md transition-colors',
                                        activeTab === t.key
                                            ? 'bg-gray-800 text-white'
                                            : 'text-gray-500 hover:bg-gray-100',
                                    )}
                                >
                                    <t.icon className="size-3" />
                                    {t.label}
                                </button>
                            ))}
                        </div>
                        <CodePanel
                            code={activeTab === 'curl' ? snippets.curl : snippets.python}
                            language={activeTab === 'curl' ? 'Bash' : 'Python'}
                        />
                    </div>

                    {/* תגובה אמיתית */}
                    {showResponse && response && (
                        <div className="flex flex-col gap-2">
                            <div className="flex items-center justify-between">
                                <span className="text-sm font-medium text-gray-700">תגובה</span>
                                <span
                                    className={cn(
                                        'text-xs font-bold px-2 py-0.5 rounded',
                                        response.status >= 200 && response.status < 300
                                            ? 'bg-green-100 text-green-700'
                                            : 'bg-red-100 text-red-700',
                                    )}
                                >
                                    HTTP {response.status}
                                </span>
                            </div>
                            <div className="relative bg-[#1e1e2e] rounded-lg text-xs">
                                <div className="flex justify-end px-3 py-1.5 bg-[#2a2a3a] border-b border-white/10">
                                    <CopyButton text={JSON.stringify(response.data, null, 2)} />
                                </div>
                                <pre className="overflow-auto p-4 max-h-64 text-green-300" dir="ltr">
                                    {JSON.stringify(response.data, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}

                    {/* דוגמת תגובה */}
                    {endpoint.response_example && !showResponse && (
                        <div className="flex flex-col gap-2">
                            <span className="text-sm font-medium text-gray-700">דוגמת תגובה</span>
                            <div className="relative bg-[#1e1e2e] rounded-lg text-xs">
                                <div className="flex justify-end px-3 py-1.5 bg-[#2a2a3a] border-b border-white/10">
                                    <CopyButton
                                        text={JSON.stringify(endpoint.response_example, null, 2)}
                                    />
                                </div>
                                <pre
                                    className="overflow-auto p-4 max-h-48 text-gray-300"
                                    dir="ltr"
                                >
                                    {JSON.stringify(endpoint.response_example, null, 2)}
                                </pre>
                            </div>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
